import { useEffect } from "react";
import { useVariant } from "@/hooks/useVariant/useVariant";
import {
  ensureIsActive,
  ensureIsActiveVariant,
} from "@/utils/product/ensureIsActive";
import { StandardProps } from "@/components/componentMap";
import { ShipFromStoreUI } from "./ShipFromStoreUI";
import { getDefaultCategory } from "@/utils/product/getDefaultCategory";
import { getOwnBrand } from "@/utils/product/getOwnBrand";
import { sanitiseTitle } from "@/components/Title/sanitiseTitle";
import { dispatchAnalyticsEvent } from "@/utils";
import { FREE_DELIVERY_THRESHOLD } from "@/constants/constants.ts";

export function ShipFromStore({
  product,
  flags,
}: Pick<StandardProps, "product" | "flags">) {
  const activeProduct = ensureIsActive(product);
  const { variant, isSelected } = useVariant(product);
  const activeVariant = ensureIsActiveVariant(variant);
  const price = activeVariant?.price?.value;
  const productId = activeProduct?.aliases.pimProductId;
  const colour = variant.differentiators?.colour;
  const size = isSelected ? variant.differentiators?.size : undefined;
  const skuId = activeVariant?.aliases.skuId;
  const ownBrand = getOwnBrand(product);
  const sanitisedTitle = sanitiseTitle(variant?.title, ownBrand);

  let freeDeliveryThreshold = FREE_DELIVERY_THRESHOLD;

  const parsed = parseInt(
    flags.freeDeliveryThresholdOverride.value?.toString() ?? "",
    10,
  );
  if (!isNaN(parsed)) {
    freeDeliveryThreshold = parsed;
  }

  useEffect(() => {
    if (isShipFromStoreNotSelected()) {
      return;
    }

    dispatchAnalyticsEvent(
      "global.impression",
      {
        type: "pdp",
        subType: "express checkout:ship from store",
        additionalInfo: {},
        properties: {},
        products: [
          {
            brand: product.brand?.name,
            category: getDefaultCategory(product),
            sku: skuId,
            price: price,
            productName: sanitisedTitle,
            productId: productId,
            colour: colour,
            size: size,
          },
        ],
      },
      {},
    );
  }, [price, productId, colour, size, skuId]);

  function isShipFromStoreNotSelected(): boolean {
    return (
      !activeProduct ||
      !activeVariant ||
      !isSelected ||
      !price ||
      !activeVariant.availability.shipFromStore
    );
  }

  if (isShipFromStoreNotSelected()) {
    return null;
  }

  return (
    price && (
      <ShipFromStoreUI
        price={price}
        freeDeliveryThreshold={freeDeliveryThreshold}
      />
    )
  );
}
